import qs from 'query-string';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import localStorage from '@utils/localStorage';
import { TEMP_DASHBOARD_URL } from '@utils/constants';
import createKeyByUserName from '@utils/tempDashboard/createKeyByUserName';
import addScrollingWindow from '@utils/addScrollingWindow';
import { setFromToByScrollingMark } from '@utils/scrollingWindowFromTo';
import { tryParse } from '@utils/tryParse';
import { CLOSE_ALL_TABS } from '@store/common/types';
import { selectedCustomerIdSelector, selectedUserNameSelector } from '../common/selectors';
import { dashboardsSelector, afterInitializationSelector } from './selectors';
import * as types from './types';
import defaultTempDashboard from './tempDashboardsStore';
import { setAfterInitialization } from './actions';
import { globalRouter } from '@src/utils/globalRouter';
import { getDrilldownUrl, openNewTab } from '@src/utils/dashboard/drilldown';
import moment from 'moment';
import { orderBy } from 'lodash';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';

function* getLocalStorageKey() {
  const customerId = yield select(selectedCustomerIdSelector);
  const userName = yield select(selectedUserNameSelector);
  return yield createKeyByUserName(types.prefix, customerId, userName);
}

function* initLocalStoreKeysIntoReducer() {
  const afterInitialization = yield select(afterInitializationSelector);
  if (afterInitialization) {
    return;
  }
  const localStorageKey = yield getLocalStorageKey();
  let localStorageTempDashboards =
    localStorage.get(localStorageKey) || cloneDeep(defaultTempDashboard);

  localStorageTempDashboards = tryParse(localStorageTempDashboards);
  setFromToByScrollingMark(localStorageTempDashboards);

  const unsavedKeys = [];
  const savedKeys = [];

  Object.keys(localStorageTempDashboards.dashboards).forEach((key) => {
    if (get(localStorageTempDashboards, `dashboards[${key}].isSaved`, false)) {
      savedKeys.push(key);
    } else {
      unsavedKeys.push(key);
    }
  });

  yield all([
    put({
      type: types.SET_KEYS,
      keys: unsavedKeys,
    }),
    // sd-note: recently saved dashboards are added here
    put({
      type: types.SET_SAVED_RECENTLY_KEYS,
      keys: savedKeys,
    }),
    put({
      type: types.SET_AFTER_INITIALIZATION,
      afterInitialization: true,
    }),
  ]);
}

export function* setStoreToLocalStorage({ type, key, value, hasChanged }) {
  const dashboards = yield select(dashboardsSelector);
  const tasks = [];
  let formatKey = key;

  if (type === types.SET_SAVED) {
    formatKey = `saved-${key}`;
  }
  if (!dashboards[formatKey]) {
    return;
  }
  // Initialize before update the localStorage
  yield initLocalStoreKeysIntoReducer();
  const localStorageKey = yield getLocalStorageKey();
  dashboards[formatKey] = {
    ...dashboards[formatKey],
    dashboard: value,
    updateTime: Date.now(),
    hasChanged,
  };
  if (hasChanged) {
    dashboards[formatKey].hasChanged = true;
    addScrollingWindow(dashboards[formatKey].dashboard);
    tasks.push(put({ type: types.REFRESH_TABS }));
  }

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
    ...tasks,
  ]);
}

function* removeStoreByKey({ key, isSaved }) {
  const dashboards = yield select(dashboardsSelector);
  let formatKey = key;
  if (isSaved) {
    formatKey = `saved-${key}`;
  }
  // Initialize before update the localStorage
  yield initLocalStoreKeysIntoReducer();
  const localStorageKey = yield getLocalStorageKey();
  if (Object.keys(dashboards).length === 1) {
    yield call(localStorage.remove, localStorageKey);
  } else {
    delete dashboards[formatKey];
    yield call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    });
  }

  if (isSaved) {
    yield put({
      type: types.REMOVE_SAVED_RECENTLY_KEY,
      key: formatKey,
    });
  } else {
    yield put({
      type: types.REMOVE_KEY,
      key: formatKey,
    });
  }
}

function* createTempDashboard({ dashboardType, dashboard, title, options = {} }) {
  // Initialize before update the localStorage
  yield initLocalStoreKeysIntoReducer();
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();
  const keys = Object.keys(dashboards).filter((key) => /^[0-9]*$/.test(key));
  const id = keys.length === 0 ? 1 : Math.max.apply(null, keys) + 1;

  dashboards[`${id}`] = {
    id: `${id}`,
    title: title || `New ${id}`,
    type: dashboardType,
    dashboard,
    createTime: Date.now(),
    updateTime: Date.now(),
    lastAccessedTime: Date.now(),
    unsaved: true,
  };

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
    put({
      type: types.SET_KEY,
      key: `${id}`,
    }),
  ]);

  const { urlSearch, redirectHandler } = options as any;
  const newBrowserTabFeature = getFeature(featureNames.NEW_BROWSER_TAB_WORKFLOW);

  if (newBrowserTabFeature && 'newBrowserTab' in options && options.newBrowserTab) {
    const url = getDrilldownUrl(dashboardType, id);
    // TODO:  handle urlSearch scenario
    openNewTab(url);
    return;
  }

  if ('redirectHandler' in options) {
    yield call(redirectHandler, { id });
  } else {
    globalRouter.navigate(
      qs.stringifyUrl({
        url: `${TEMP_DASHBOARD_URL[dashboardType]}${id}`,
        query: urlSearch,
      }),
    );
  }
}

function* updateDashboardTitle({ oldTitle, newTitle }) {
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();
  const dashboard = Object.values(dashboards).find((d) => d.title === oldTitle);
  if (dashboard) {
    dashboard.title = newTitle;
    yield all([
      call(localStorage.set, {
        key: localStorageKey,
        value: { dashboards },
      }),
      put({
        type: types.SET_KEY,
        key: `${dashboard.id}`,
      }),
    ]);
  }
}

function* updateTempDashboardByTitle({ oldTitle, newTitle, val }) {
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();
  const dashboard: any = Object.values(dashboards).find((d) => d.title === oldTitle);
  if (dashboard) {
    dashboard.title = newTitle;
    dashboard.dashboard = val;
    yield all([
      call(localStorage.set, {
        key: localStorageKey,
        value: { dashboards },
      }),
      put({
        type: types.SET_KEY,
        key: `${dashboard.id}`,
      }),
    ]);
  }
}

// sd-note: this gets called from UpdateProvider in the DashboardHOC which all the pages use
function* createSavedDashboard({ id, dashboardName, dashboardType, dashboard }) {
  // Initialize before update the localStorage

  // sd-note: this gets called when a new saved dashboard is created
  yield initLocalStoreKeysIntoReducer();
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();
  const key = `saved-${id}`;
  dashboards[key] = {
    id: `${id}`,
    title: dashboardName,
    type: dashboardType,
    isSaved: true,
    hasChanged: false,
    dashboard,
    createTime: Date.now(),
    updateTime: Date.now(),
  };

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
    put({
      type: types.SET_SAVED_RECENTLY_KEY,
      key,
    }),
  ]);
}

function* setHasChangedById({ id, name }) {
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();
  const key = `saved-${id}`;
  if (!dashboards[key]) {
    return;
  }
  dashboards[key] = {
    ...dashboards[key],
    hasChanged: false,
  };

  if (!!name && get(dashboards, `[${key}].title`, '') !== name) {
    dashboards[key].title = name;
  }

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
  ]);
  yield put({ type: types.REFRESH_TABS });
}

function* resetSavedDashboard({ id, dashboard }) {
  const customerId = yield select(selectedCustomerIdSelector);
  const dashboards = yield select(dashboardsSelector);
  const key = `saved-${id}`;
  if (!dashboards[key]) {
    return;
  }
  dashboards[key] = {
    ...dashboards[key],
    dashboard,
    hasChanged: false,
  };

  yield all([
    call(localStorage.set, {
      key: `${types.prefix}-${customerId}`,
      value: { dashboards },
    }),
  ]);
  yield put({ type: types.REFRESH_TABS });
}

function* updateAccessedTime({ id }) {
  // console.log('updateAccessedTime::started', id);

  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();

  if (!(id in dashboards)) {
    return;
  }

  dashboards[id].lastAccessedTime = Date.now();

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
  ]);
}

function* cleanTempDashboards() {
  const dashboards = yield select(dashboardsSelector);
  const localStorageKey = yield getLocalStorageKey();

  // console.log('cleanTempDashboards', dashboards);

  const MAX_UNUSED_DAYS_LIMIT = 7;
  const currentTime = moment(Date.now());

  // remove temp dashboards that have not been accessed recently
  Object.keys(dashboards).forEach((key) => {
    const { lastAccessedTime = null } = dashboards[key];
    if (lastAccessedTime) {
      const diff = currentTime.diff(lastAccessedTime, 'd');
      if (diff > MAX_UNUSED_DAYS_LIMIT) {
        delete dashboards[key];
        // TODO: log event?
        // console.log('BY DATE: cleaning up dashboard', key, diff);
      }
    } else {
      // set default access date
      dashboards[key].lastAccessedTime = Date.now();
    }
  });

  // removed based on accessed time (oldest) if there are too many recently used ones
  const MAX_TEMP_DASHBOARD_LIMIT = 5;
  const count = Object.keys(dashboards).length;
  if (count > MAX_TEMP_DASHBOARD_LIMIT) {
    const orderedDashboardList = orderBy(dashboards, ['lastAccessedTime'], ['desc']);
    for (let i = count; i >= MAX_TEMP_DASHBOARD_LIMIT; i--) {
      const idToRemove = orderedDashboardList.pop();
      // console.log('BY LIMIT: cleaning up dashboard', idToRemove, i);
    }
  }

  yield all([
    call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    }),
  ]);
  yield put({ type: types.REFRESH_TABS });
}

function* closeAllTempTabs() {
  const localStorageKey = yield getLocalStorageKey();

  const defaultLandingPageState = useDefaultLandingPageStore.getState();
  const defaultLandingPage = {
    url: defaultLandingPageState.url,
    path: defaultLandingPageState.path,
    name: defaultLandingPageState.name,
  };

  const localStorageTempDashboards = tryParse(localStorage.get(localStorageKey));

  // For https://conviva.atlassian.net/browse/PUI-1669
  // Saved dashboard should be kept if it's the default landing page.
  const savedLandingPageKey = `saved-${defaultLandingPage?.name}`;
  const defaultDashboard = get(localStorageTempDashboards, `dashboards.${savedLandingPageKey}`);
  if (defaultDashboard) {
    const dashboards = { [savedLandingPageKey]: defaultDashboard };
    yield call(localStorage.set, {
      key: localStorageKey,
      value: { dashboards },
    });
  } else {
    yield call(localStorage.remove, localStorageKey);
  }

  yield put(setAfterInitialization(false));
  yield initLocalStoreKeysIntoReducer();
}

function* watchInit() {
  yield takeLatest([types.INIT, types.INIT_AT_COMPONENT], initLocalStoreKeysIntoReducer);
}

function* watchSet() {
  yield takeLatest([types.SET, types.SET_SAVED], setStoreToLocalStorage);
}

function* watchRemove() {
  yield takeLatest([types.REMOVE], removeStoreByKey);
}

function* watchCreateTempDashboard() {
  yield takeLatest([types.CREATE_TEMP_DASHBOARD], createTempDashboard);
}

function* watchUpdateDashboardTitle() {
  yield takeLatest([types.UPDATE_DASHBOARD_TITLE], updateDashboardTitle);
}

function* watchUpdateTempDashboardByTitle() {
  yield takeLatest([types.UPDATE_TEMP_DASHBOARD_BY_TITLE], updateTempDashboardByTitle);
}

function* watchCreateSavedDashboard() {
  yield takeLatest([types.CREATE_SAVED_DASHBOARD], createSavedDashboard);
}

function* watchSetHasChangedById() {
  yield takeLatest([types.UPDATED_SAVED_DASHBOARD], setHasChangedById);
}

function* watchResetSavedDashboard() {
  yield takeLatest([types.RESET_SAVED_DASHBOARD], resetSavedDashboard);
}

function* watchCloseAllTabs() {
  yield takeLatest(CLOSE_ALL_TABS, closeAllTempTabs);
}

function* watchUpdateAccessedTime() {
  yield takeLatest([types.UPDATE_ACCESSED_TIME], updateAccessedTime);
}

function* watchCleanTempDashboards() {
  yield takeLatest([types.CLEAN_TEMP_DASHBOARDS], cleanTempDashboards);
}

export default [
  watchInit,
  watchSet,
  watchRemove,
  watchCreateTempDashboard,
  watchCreateSavedDashboard,
  watchSetHasChangedById,
  watchResetSavedDashboard,
  watchUpdateDashboardTitle,
  watchCloseAllTabs,
  watchUpdateAccessedTime,
  watchCleanTempDashboards,
];
